import React, { useState } from 'react';
import { toast } from 'sonner';
import { authFetch } from '../../utils/auth';
import PasswordStrengthField from '../security/PasswordStrengthField';
import { PASSWORD_MIN_LENGTH } from '../../utils/passwordStrength';

/**
 * Section "Sécurité" — changement de mot de passe self-service.
 * Politique NIST 2024 : min 10 caractères + contrôle HIBP + indicateur de force.
 * Utilise l'endpoint POST /api/auth/change-password.
 */
const ChangePasswordSection = () => {
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [validity, setValidity] = useState({ tooShort: true, pwned: false, checking: false });
  const [saving, setSaving] = useState(false);

  const mismatch = confirmPwd.length > 0 && newPwd !== confirmPwd;
  const canSubmit = !!oldPwd && !validity.tooShort && !validity.pwned && !validity.checking
    && newPwd === confirmPwd && newPwd.length >= PASSWORD_MIN_LENGTH;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      if (newPwd.length < PASSWORD_MIN_LENGTH) toast.error(`Le nouveau mot de passe doit faire au moins ${PASSWORD_MIN_LENGTH} caractères.`);
      else if (validity.pwned) toast.error('Ce mot de passe est compromis, choisissez-en un autre.');
      else if (newPwd !== confirmPwd) toast.error('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setSaving(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ old_password: oldPwd, new_password: newPwd }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Mot de passe modifié avec succès.');
        setOldPwd(''); setNewPwd(''); setConfirmPwd('');
      } else {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Erreur lors de la modification.');
      }
    } catch {
      toast.error('Erreur de connexion au serveur.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="securite" className="bg-white rounded-lg shadow-sm p-6 scroll-mt-8" data-testid="security-section">
      <h2 className="text-xl font-semibold mb-1 text-gray-900 flex items-center gap-2">
        <i className="bi bi-shield-lock"></i>
        Sécurité
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        Changez votre mot de passe. Une seule session active est autorisée par compte.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4 max-w-md" data-testid="change-password-form">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe actuel</label>
          <input
            type="password" value={oldPwd} onChange={(e) => setOldPwd(e.target.value)} required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF] focus:border-[#007BFF]"
            data-testid="change-password-old"
          />
        </div>
        <PasswordStrengthField
          value={newPwd}
          onChange={setNewPwd}
          label="Nouveau mot de passe"
          testid="change-password-new"
          onValidityChange={setValidity}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Confirmer le nouveau mot de passe</label>
          <input
            type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF] focus:border-[#007BFF]"
            data-testid="change-password-confirm"
          />
          {mismatch && (
            <p className="text-xs text-[#DC2626] mt-1.5" data-testid="change-password-mismatch">
              <i className="bi bi-exclamation-circle mr-1"></i>Les mots de passe ne correspondent pas.
            </p>
          )}
        </div>
        <button
          type="submit" disabled={saving || !canSubmit}
          className="px-6 py-2 bg-[#28A745] hover:bg-[#218838] text-white rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          data-testid="change-password-submit"
        >
          {saving ? <><i className="bi bi-arrow-repeat animate-spin"></i>Enregistrement…</> : <><i className="bi bi-check2"></i>Mettre à jour</>}
        </button>
      </form>
    </div>
  );
};

export default ChangePasswordSection;
